/**
 * TestModeProvider - Isolierte Test-Mode-Logik
 *
 * Kapselt alle Test-Mode-spezifische Logik an einem Ort,
 * anstatt sie über den gesamten Code zu verstreuen.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo
} from 'react';
import { useFeature, useFeatureFlags } from '../FeatureProvider';
import { useGameDispatch } from '../../state/GameContext';

// Context
const TestModeContext = createContext(null);

/**
 * TestModeProvider Component
 */
export function TestModeProvider({ children }) {
  const isTestModeEnabled = useFeature('TEST_MODE');
  const { setFeatureEnabled } = useFeatureFlags();
  const dispatch = useGameDispatch();

  // Test-Mode State - wird immer initialisiert (Hooks-Regeln)
  const [virtualPlayers, setVirtualPlayers] = useState([]);
  const [activePlayerIndex, setActivePlayerIndex] = useState(0);
  const [playerCount, setPlayerCount] = useState(2);
  const [autoPlay, setAutoPlay] = useState(false);
  const [showAllHands, setShowAllHands] = useState(true);

  /**
   * Erstellt virtuelle Spieler für den Test-Modus
   */
  const createVirtualPlayers = useCallback((count) => {
    const players = [];

    // Erster Spieler ist der echte Spieler
    players.push({
      id: 'local-player',
      name: 'Du',
      nickname: 'Du',
      isVirtual: false,
      index: 0,
    });

    // Virtuelle Spieler erstellen
    for (let i = 1; i < count; i++) {
      players.push({
        id: `virtual-${i}`,
        name: `Bot ${i}`,
        nickname: `Bot ${i}`,
        isVirtual: true,
        index: i,
      });
    }

    setVirtualPlayers(players);
    setPlayerCount(count);

    return players;
  }, []);

  /**
   * Wechselt zum nächsten Spieler (für manuelles Durchspielen)
   */
  const cycleToNextPlayer = useCallback(() => {
    setActivePlayerIndex(prev => (prev + 1) % playerCount);
  }, [playerCount]);

  /**
   * Wechselt zu einem bestimmten Spieler
   */
  const switchToPlayer = useCallback((index) => {
    if (index >= 0 && index < playerCount) {
      setActivePlayerIndex(index);
    }
  }, [playerCount]);

  /**
   * Aktiviert/Deaktiviert Auto-Play für Bots
   */
  const toggleAutoPlay = useCallback(() => {
    setAutoPlay(prev => !prev);
  }, []);

  /**
   * Aktiviert/Deaktiviert Anzeige aller Hände
   */
  const toggleShowAllHands = useCallback(() => {
    setShowAllHands(prev => !prev);
  }, []);

  /**
   * Aktiviert den Test-Modus
   */
  const enableTestMode = useCallback((options = {}) => {
    const count = options.playerCount || 2;

    setFeatureEnabled('TEST_MODE', true);
    createVirtualPlayers(count);
    setActivePlayerIndex(0);

    // Nur dispatchen wenn verfügbar
    if (dispatch) {
      dispatch({
        type: 'SET_TEST_MODE',
        payload: {
          enabled: true,
          playerCount: count,
        },
      });
    }
  }, [setFeatureEnabled, createVirtualPlayers, dispatch]);

  /**
   * Deaktiviert den Test-Modus
   */
  const disableTestMode = useCallback(() => {
    setFeatureEnabled('TEST_MODE', false);
    setVirtualPlayers([]);
    setActivePlayerIndex(0);
    setAutoPlay(false);

    // Nur dispatchen wenn verfügbar
    if (dispatch) {
      dispatch({
        type: 'SET_TEST_MODE',
        payload: {
          enabled: false,
          playerCount: 0,
        },
      });
    }
  }, [setFeatureEnabled, dispatch]);

  /**
   * Simuliert einen Bot-Zug (für Auto-Play)
   */
  const simulateBotMove = useCallback((gameState) => {
    if (!autoPlay || activePlayerIndex === 0) {
      return null; // Echter Spieler oder Auto-Play deaktiviert
    }

    // TODO: Bot-KI implementieren
    // Vorerst: zufällige Aktion (pass oder draw)
    const actions = ['pass', 'draw'];
    const randomAction = actions[Math.floor(Math.random() * actions.length)];

    return {
      action: randomAction,
      playerIndex: activePlayerIndex,
    };
  }, [autoPlay, activePlayerIndex]);

  /**
   * Gibt Spieler-Daten für einen Index zurück
   */
  const getPlayerData = useCallback((index) => {
    return virtualPlayers[index] || null;
  }, [virtualPlayers]);

  /**
   * Prüft ob ein Spieler virtuell ist
   */
  const isVirtualPlayer = useCallback((index) => {
    return virtualPlayers[index]?.isVirtual ?? false;
  }, [virtualPlayers]);

  // Context-Wert (immer berechnet, damit Hooks-Regeln eingehalten werden)
  const value = useMemo(() => {
    // Minimaler Wert wenn Test-Mode nicht aktiv
    if (!isTestModeEnabled) {
      return {
        isTestMode: false,
        enableTestMode,
        disableTestMode,
      };
    }

    // Vollständiger Wert wenn Test-Mode aktiv
    return {
      // Status
      isTestMode: true,
      playerCount,
      activePlayerIndex,
      autoPlay,
      showAllHands,

      // Spieler
      virtualPlayers,
      getPlayerData,
      isVirtualPlayer,

      // Aktionen
      enableTestMode,
      disableTestMode,
      createVirtualPlayers,
      cycleToNextPlayer,
      switchToPlayer,
      toggleAutoPlay,
      toggleShowAllHands,
      simulateBotMove,

      // Setters
      setPlayerCount,
      setActivePlayerIndex,
    };
  }, [
    isTestModeEnabled,
    playerCount,
    activePlayerIndex,
    autoPlay,
    showAllHands,
    virtualPlayers,
    getPlayerData,
    isVirtualPlayer,
    enableTestMode,
    disableTestMode,
    createVirtualPlayers,
    cycleToNextPlayer,
    switchToPlayer,
    toggleAutoPlay,
    toggleShowAllHands,
    simulateBotMove,
  ]);

  return (
    <TestModeContext.Provider value={value}>
      {children}
    </TestModeContext.Provider>
  );
}

/**
 * Hook zum Zugriff auf Test-Mode
 */
export function useTestMode() {
  const context = useContext(TestModeContext);
  if (!context) {
    throw new Error('useTestMode must be used within a TestModeProvider');
  }
  return context;
}

/**
 * Hook der nur prüft ob Test-Mode aktiv ist
 */
export function useIsTestMode() {
  const { isTestMode } = useTestMode();
  return isTestMode;
}

/**
 * TestModeControls - UI-Komponente für Test-Mode-Steuerung
 */
export function TestModeControls() {
  const testMode = useTestMode();

  if (!testMode.isTestMode) {
    return null;
  }

  const {
    playerCount,
    activePlayerIndex,
    autoPlay,
    showAllHands,
    cycleToNextPlayer,
    switchToPlayer,
    toggleAutoPlay,
    toggleShowAllHands,
    setPlayerCount,
    createVirtualPlayers,
  } = testMode;

  const handlePlayerCountChange = (e) => {
    const count = parseInt(e.target.value, 10);
    if (count >= 2 && count <= 6) {
      setPlayerCount(count);
      createVirtualPlayers(count);
    }
  };

  return (
    <div className="test-mode-controls" style={styles.container}>
      <div style={styles.header}>
        <span style={styles.badge}>TEST MODE</span>
      </div>

      <div style={styles.controls}>
        <div style={styles.control}>
          <label>Spieler:</label>
          <select value={playerCount} onChange={handlePlayerCountChange}>
            {[2, 3, 4, 5, 6].map(n => (
              <option key={n} value={n}>{n} Spieler</option>
            ))}
          </select>
        </div>

        <div style={styles.control}>
          <label>Aktiv:</label>
          <div style={styles.playerButtons}>
            {Array.from({ length: playerCount }).map((_, i) => (
              <button
                key={i}
                style={{
                  ...styles.playerButton,
                  ...(i === activePlayerIndex ? styles.activePlayer : {}),
                }}
                onClick={() => switchToPlayer(i)}
              >
                {i === 0 ? 'Du' : `Bot ${i}`}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.control}>
          <button onClick={cycleToNextPlayer} style={styles.button}>
            Nächster →
          </button>
        </div>

        <div style={styles.toggles}>
          <label style={styles.toggle}>
            <input
              type="checkbox"
              checked={autoPlay}
              onChange={toggleAutoPlay}
            />
            Auto-Play Bots
          </label>

          <label style={styles.toggle}>
            <input
              type="checkbox"
              checked={showAllHands}
              onChange={toggleShowAllHands}
            />
            Alle Hände zeigen
          </label>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed',
    top: '10px',
    right: '10px',
    background: 'rgba(0, 0, 0, 0.85)',
    borderRadius: '8px',
    padding: '10px',
    zIndex: 9999,
    color: 'white',
    fontSize: '12px',
    maxWidth: '250px',
  },
  header: {
    marginBottom: '8px',
  },
  badge: {
    background: '#e94560',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 'bold',
  },
  controls: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  control: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  playerButtons: {
    display: 'flex',
    gap: '4px',
  },
  playerButton: {
    padding: '2px 6px',
    fontSize: '10px',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: '4px',
    color: 'white',
    cursor: 'pointer',
  },
  activePlayer: {
    background: '#e94560',
  },
  button: {
    padding: '4px 8px',
    fontSize: '11px',
    background: '#e94560',
    border: 'none',
    borderRadius: '4px',
    color: 'white',
    cursor: 'pointer',
  },
  toggles: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: '4px',
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
  },
};

export default TestModeProvider;
